import * as React from 'react';
import { ReactNode } from 'react';
import * as Model from '@mms-v3/data-model-js';

import getLogger, { Logger } from '../../log';
import { checkIsOwnRecord, checkReadPermission, checkWriteObligation } from 'Auth/utils/DataModel';
import { AuthContext } from './AuthProvider';
import { SessionState } from '../utils/interfaces';

const logger = getLogger('Auth/PermissionGate');

export type WritableParam = false | Exclude<Model.WriteObligationValue, 'NONE'>;
export type ReadWritePermissions = {
  canRead: boolean;
  canWrite: boolean;
  required: boolean;
  field?: string;
};

export type RenderChild = (
  checkPerm: (child?: string) => ReadWritePermissions,
) => React.ReactNode;

export type CheckPermissionFunction = (child?: string) => ReadWritePermissions;

interface Props {
  entity?: string;
  field?: string;
  always?: boolean;
  emails?: string[];
  new?: boolean;
  children?: RenderChild;
}

/**
 * The PermissionGate function is a React component that controls the rendering of its children based on user
 * permissions. It uses an Authorizer to check if the user has the necessary read and write permissions for a
 * specific entity and field in the data model. If the permissions are met, it renders the children; otherwise,
 * it returns null.
 *
 * @param props
 * entity: The name of the entity to check permissions for.
 * field: The specific field within the entity to check.
 * always: A boolean indicating if the children should always be rendered regardless of permissions.
 * emails: An array of email addresses to check ownership.
 * new: A boolean indicating if the entity is new.
 * children: A render function that receives the permissions and a function to check permissions for child fields.
 * @constructor
 */
export function PermissionGate(props: Props): ReactNode {

  /**
   * a function renderInner that checks user permissions for a specific entity and field in a data model. It uses an
   * Authorizer to determine if the user has the necessary read and write permissions and returns the appropriate
   * content based on these permissions.
   *
   * @param session
   */
  const renderInner = (session: SessionState): React.ReactNode => {
    const always: boolean = !!props.always;
    const emails: string[] = props.emails || [];
    const _new: boolean = !!props.new;
    const {entity, field} = props;

    const Entity: Model.Entity = Model.DataModel.for(entity);
    if (Entity === null) {
      logger.error('No entity definition for', entity);
      return null;
    }

    /**
     * if no permissions exist for the field, all permissions are denied.
     */
    const Field: Model.Field = Entity.field(field);
    if (Field == null) {
      // logger.debug('No attribute definition for', entity, field);
      return null;
    }

    const roles: string[] = session?.roles || [];
    const isOwnRecord: boolean = checkIsOwnRecord(session, _new, emails);

    const checkPermissions = (child: string = null): ReadWritePermissions => {
      const fieldData: Model.Field = Field.field(child);
      if (!fieldData) {
        return {
          canRead: false,
          canWrite: false,
          required: false,
          field: `${field}-${child}`,
        };
      }
      return {
        canRead: checkReadPermission(fieldData, roles, isOwnRecord),
        canWrite: checkWriteObligation(fieldData, roles, isOwnRecord) !== false,
        required: checkWriteObligation(fieldData, roles, isOwnRecord) === 'MANDATORY',
        field: `${field ? field : ''}-${child ? child : ''}`,
      };
    };

    return always || checkPermissions().canRead ? props.children(checkPermissions) : null;
  };

  return <AuthContext.Consumer>
    {({session}) => renderInner(session)}
  </AuthContext.Consumer>;
}
